import React, { useEffect } from "react";
import clsx from "clsx";
import { debounce } from "../Dropdown";
import styles from "./index.module.scss";

export function aspectFitScaleFromRect(
  width: number,
  height: number,
  targetWidth: number,
  targetHeight: number
) {
  const ratio = width / height;
  const targetRatio = targetWidth / targetHeight;
  if (ratio > targetRatio) {
    return {
      targetRatio,
      ratio,
      width: targetWidth,
      height: targetWidth / ratio,
    };
  }
  return {
    targetRatio,
    ratio,
    width: targetHeight * ratio,
    height: targetHeight,
  };
}
interface IProps {
  childrenWidth: number;
  childrenHeight: number;
  wrapperChildren?:React.ReactNode;
  children:
    | React.ReactNode
    | ((width: number, height: number) => React.ReactNode);
  className?: string;
  innerClassName?: string;
  innerStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  onChange?: () => void;
}

export default function FitSize(props: IProps) {
  const {
    childrenWidth,
    childrenHeight,
    className,
    innerClassName,
    innerStyle,
    style,
    wrapperChildren,
  } = props;
  const [width, setWidth] = React.useState(0);
  const [height, setHeight] = React.useState(0);
  const [wrapperRatio, setWrapperRatio] = React.useState(0);
  const [innerRatio, setInnerRatio] = React.useState(0);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const resize = () => {
      if (!wrapperRef.current) return;
      const { clientHeight, clientWidth } = wrapperRef.current;
      if (clientWidth === 0 || clientHeight === 0) {
        setTimeout(() => {
          resize();
        }, 100);
      }
      const newSize = aspectFitScaleFromRect(
        childrenWidth,
        childrenHeight,
        clientWidth,
        clientHeight
      );
      setWidth(Math.floor(newSize.width));
      setHeight(Math.floor(newSize.height));
      setWrapperRatio(newSize.targetRatio);
      setInnerRatio(newSize.ratio);
    };
    resize();
    const debounceResize = debounce(resize, 100);
    window.addEventListener("resize", debounceResize);
    return () => {
      window.removeEventListener("resize", debounceResize);
    };
  }, [childrenWidth, childrenHeight]);
  const innerIsVertical = wrapperRatio > innerRatio;

  useEffect(() => {
    props.onChange?.();
  }, [width, height]);

  return (
    <div
      className={clsx(styles.fitSize, className)}
      ref={wrapperRef}
      style={style}
    >
      <div
        className={clsx(
          styles.fitSizeInner,
          innerIsVertical && styles.verticel,
          !innerIsVertical && styles.not_verticel,
          innerClassName
        )}
        style={{ width, height, ...innerStyle }}
      >
        {typeof props.children === "function"
          ? props.children(width, height)
          : props.children}
      </div>
      {wrapperChildren}
    </div>
  );
}
